import React, { Component } from 'react';
import queryString from 'query-string';
import styles from './styles.sass';
import { AnchorAPI } from '../../modules/AnchorAPI';
import { Spinner } from '../../shared/Spinner';
import Box from '../../shared/Box/index.tsx';
import Icon from '../../shared/Icon/index.tsx';
import { CallJoinContent } from './CallJoinContent/index.tsx';
import { CurrentlyOnCall } from './CurrentlyOnCall/index.tsx';
import { CallEndedOrFull } from './CallEndedOrFull/index.tsx';
import { windowUndefined } from '../../../helpers/serverRenderingUtils';

// PRODUCTION
const API_KEY = '45975412';
// development;
// const API_KEY = '45966662';

const setupGlobalStyles = () => {
  document.querySelector('html').classList.add(styles.html);
  document.querySelector('body').classList.add(styles.body);
  document.querySelector('#app').classList.add(styles.app);
  document.querySelector('#app > div').classList.add(styles.appChild);
  document
    .querySelector('#app > div > div')
    .classList.add(styles.appChildChild);
};

const cleanGlobalStyles = () => {
  document.querySelector('html').classList.remove(styles.html);
  document.querySelector('body').classList.remove(styles.body);
  document.querySelector('#app').classList.remove(styles.app);
  document.querySelector('#app > div').classList.remove(styles.appChild);
  document
    .querySelector('#app > div > div')
    .classList.remove(styles.appChildChild);
};

const getUUIDFromData = data => queryString.parse(data).uuid;

const Header = () => (
  <div className={styles.navBar}>
    <Box
      maxWidth={1400}
      width="100%"
      lgHeight={96}
      mdHeight={76}
      smHeight={76}
      display="flex"
      alignItems="center"
    >
      <Box>
        <Box display="flex" alignItems="center">
          <Box marginLeft={3} smWidth={114} mdWidth={124} lgWidth={126}>
            <Icon type="AnchorNameLogoWithTrademarkIcon" fillColor="white" />
          </Box>
        </Box>
      </Box>
    </Box>
  </div>
);

class CallJoin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scene: 'loading',
      displayName: null,
      conferenceCallId: null,
      participants: [],
      publisher: null,
      session: null,
      hostPodcastName: '',
      hostPodcastImage: '',
      isShowingRecordingDoneConfirmation: false,
      email: '',
      isFetching: false,
      isNetworkConnected: true,
      hostName: '',
      deviceId: null,
      selfUUID: null,
    };
  }

  handleNetworkReconnect = () => {
    this.setState(() => ({
      isNetworkConnected: true,
    }));
  };

  handleNetworkDisconnect = () => {
    this.setState(() => ({
      isNetworkConnected: false,
    }));
  };

  componentDidMount() {
    const {
      inviteCode,
      user: { user },
    } = this.props;
    setupGlobalStyles();
    const isUserLoggedIn = !!user;
    const displayName = isUserLoggedIn ? user.name : '';
    this.setState(
      () => ({
        displayName,
      }),
      () => {
        this.setupPage({
          displayName,
          inviteCode,
          isUserLoggedIn,
        });
      }
    );
  }

  componentWillUnmount() {
    const { session, publisher } = this.state;
    cleanGlobalStyles();
    if (session && publisher) {
      session.unpublish(publisher);
    }
  }

  handleComplete = err => {
    if (err) this.setState({ isErrorModalOpen: true, isFetching: false });
  };

  setupOpenTok = ({ sessionId, token, conferenceCallId, isUserLoggedIn }) => {
    if (windowUndefined()) throw new Error('window not defined');

    const { deviceId } = this.state;
    const { onPressDoneRecordingConfirmation } = this.props;

    const OT = require('@opentok/client');
    const session = OT.initSession(API_KEY, sessionId);

    const publisher = OT.initPublisher(
      undefined,
      {
        videoSource: null,
        publishAudio: true,
        publishVideo: false,
        audioSource: deviceId,
      },
      this.handleComplete
    );

    session.connect(token, connectError => {
      if (connectError) {
        // something went wrong, but we currently dont handle this
      } else {
        // after the user connects, store their UUID this will be used
        // to identify which participant is them so we can indicate it as 'you'
        this.setState({
          selfUUID: getUUIDFromData(session.connection.data),
        });
        session.publish(publisher, publishError => {
          if (publishError) {
            // something went wrong, but we currently dont handle this
          }
        });
      }
    });

    session.on('streamCreated', event => {
      session.subscribe(event.stream, 'subscriber', {}, err => {
        if (err) {
          // something went wrong, but we currently dont handle this
        }
      });
    });

    session.on('connectionCreated', event => {
      const userUUID = getUUIDFromData(event.connection.data);
      AnchorAPI.fetchConferenceCallParticipants(conferenceCallId).then(
        response => {
          const { participants } = response;
          this.setState(prevState => ({
            participants: [
              ...prevState.participants,
              participants.find(p => p.uuid === userUUID),
            ],
          }));
        }
      );
    });

    session.on('connectionDestroyed', event => {
      AnchorAPI.fetchConferenceCallParticipants(conferenceCallId).then(
        response => {
          const host = response.participants.find(
            participant => participant.isHost
          );
          const userUUID = getUUIDFromData(event.connection.data);
          if (host.uuid && userUUID === host.uuid) {
            onPressDoneRecordingConfirmation(isUserLoggedIn);
            session.unpublish(publisher);
          } else {
            this.setState(state => ({
              participants: state.participants.filter(p => p.uuid !== userUUID),
            }));
          }
        }
      );
    });
    this.setState({ session, publisher });
  };

  setupRWFCall = ({
    inviteCode,
    notLoggedInUserName,
    conferenceCallId,
    isUserLoggedIn,
    email,
  }) => {
    AnchorAPI.joinRWFCall(
      conferenceCallId,
      inviteCode,
      notLoggedInUserName,
      isUserLoggedIn,
      email
    ).then(response => {
      const { sessionId, token, uuid } = response;
      AnchorAPI.registerSessionConnection({
        sessionId,
        ...(isUserLoggedIn
          ? {}
          : {
              anonUserUUID: uuid,
            }),
        conferenceCallId,
      });
      this.setupOpenTok({
        sessionId,
        token,
        inviteCode,
        conferenceCallId,
        isUserLoggedIn,
      });
    });
  };

  handleSubmit = ({
    inviteCode,
    notLoggedInUserName,
    conferenceCallId,
    isUserLoggedIn,
    email,
  }) => {
    this.setState(() => ({
      scene: 'activeAnonymous',
      isFetching: true,
    }));
    AnchorAPI.fetchConferenceCallInfo(inviteCode).then(response => {
      const { status } = response;
      if (status === 'ended') {
        this.setState(() => ({
          participants: [],
          scene: 'ended',
        }));
      } else {
        this.setupRWFCall({
          inviteCode,
          notLoggedInUserName,
          conferenceCallId,
          isUserLoggedIn,
          email,
        });
      }
    });
  };

  setupPageForActiveConferenceCall = ({
    isUserLoggedIn,
    hostPodcastImage,
    hostPodcastName,
  }) => {
    this.setState(() => ({
      scene: isUserLoggedIn ? 'activeLoggedIn' : 'displayName',
      hostPodcastImage,
      hostPodcastName,
    }));
  };

  setupPageForMaximumReached = () => {
    this.setState(() => ({
      scene: 'maximumReached',
    }));
  };

  setupPage = ({ displayName, inviteCode, isUserLoggedIn }) => {
    AnchorAPI.fetchConferenceCallInfo(inviteCode)
      .then(response => {
        const {
          conferenceCallId,
          status,
          hostPodcastImage,
          hostPodcastName,
          hostName,
        } = response;
        this.setState(() => ({
          conferenceCallId,
          hostPodcastImage,
          hostPodcastName,
          hostName,
        }));
        switch (status) {
          case 'active':
            this.setupPageForActiveConferenceCall({
              displayName,
              inviteCode,
              isUserLoggedIn,
              conferenceCallId,
              hostPodcastImage,
              hostPodcastName,
            });
            break;
          case 'maximumReached':
            return this.setupPageForMaximumReached();
          default:
            throw new Error(`Need to handle status: ${status}`);
        }
      })
      .catch(err => {
        // NOTE: Endpoint currently throws a 500 (internal server error) when the invite is not longer active
        this.setState(() => ({
          scene: 'ended',
        }));
      });
  };

  handleChange = value => {
    this.setState({ displayName: value });
  };

  renderLoadingScene = () => (
    <Box
      width="100%"
      display="flex"
      justifyContent="center"
      paddingTop={10}
      height="87vh"
      color="white"
    >
      <Box width={30}>
        <Spinner />
      </Box>
    </Box>
  );

  renderParticipantList = participants => (
    <Box>
      {participants.map(participant => (
        <Box>{participant.displayName}</Box>
      ))}
    </Box>
  );

  handleCloseErrorModal = () => {
    this.setState(() => ({
      isErrorModalOpen: false,
    }));
  };

  handleReadyForRecording = microphone => {
    this.setState(() => ({
      deviceId: microphone.deviceId,
    }));
  };

  renderActiveScene = (
    isUserLoggedIn,
    participants,
    inviteCode,
    conferenceCallId
  ) => {
    const { station, user } = this.props;
    const {
      displayName,
      hostPodcastName,
      hostPodcastImage,
      email,
      isFetching,
      isErrorModalOpen,
    } = this.state;
    return (
      <CallJoinContent
        onReadyForRecording={this.handleReadyForRecording}
        onCloseErrorModal={this.handleCloseErrorModal}
        isErrorModalOpen={isErrorModalOpen}
        isUserLoggedIn={isUserLoggedIn}
        participants={participants}
        podcastCreatorUserName={
          station && station.creator && station.creator.name
            ? station.creator.name
            : ''
        }
        loggedInUserName={
          user && user.user && user.user.name ? user.user.name : ''
        }
        notLoggedInUserName={displayName}
        podcastName={hostPodcastName}
        inviteCode={inviteCode}
        conferenceCallId={conferenceCallId}
        onSubmit={this.handleSubmit}
        onChangeDisplayName={this.handleChange}
        onChangeMicrophone={deviceId => {
          this.setState(() => ({
            deviceId,
          }));
        }}
        coverartImageUrl={hostPodcastImage}
        onChangeEmail={changedEmail => {
          this.setState({
            email: changedEmail,
          });
        }}
        email={email}
        isFetching={isFetching}
      />
    );
  };

  render() {
    const {
      inviteCode,
      user: { user },
      openInAppUrl,
      onPressDoneRecordingConfirmation,
    } = this.props;
    const isUserLoggedIn = !!user;
    const {
      scene,
      conferenceCallId,
      participants,
      session,
      publisher,
      isNetworkConnected,
      hostName,
      hostPodcastName,
      isShowingRecordingDoneConfirmation,
      hostPodcastImage,
      selfUUID,
    } = this.state;
    if (participants.length > 0 && scene !== 'ended') {
      return (
        <Box
          width="100%"
          height="100%"
          display="flex"
          direction="column"
          color="white"
        >
          <Box
            width="100%"
            height="100%"
            display="flex"
            direction="column"
            lgMaxHeight={800}
          >
            <Header />
            <Box isFlexChildAndExpandsToAvailableSpace>
              <Box height="100%" display="flex">
                <CurrentlyOnCall
                  session={session}
                  publisher={publisher}
                  participants={participants}
                  onPressLeave={() => {
                    session.disconnect();
                    this.setState(() => ({
                      participants: [],
                      scene: 'ended',
                      isShowingRecordingDoneConfirmation: true,
                    }));
                  }}
                  isNetworkConnected={isNetworkConnected}
                  onNetworkReconnected={this.handleNetworkReconnect}
                  onNetworkDisconnected={this.handleNetworkDisconnect}
                  selfUUID={selfUUID}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box height="100%" color="white">
        <Header />
        {(() => {
          switch (scene) {
            case 'loading':
              return this.renderLoadingScene();
            case 'activeAnonymous':
            case 'activeLoggedIn':
            case 'displayName':
              return this.renderActiveScene(
                isUserLoggedIn,
                participants,
                inviteCode,
                conferenceCallId
              );
            case 'ended':
              return (
                <CallEndedOrFull
                  openInAppUrl={openInAppUrl}
                  scene="ended"
                  podcastCreatorUserName={hostName}
                  podcastName={hostPodcastName}
                  isShowingLeaveConfirmationModal={
                    isShowingRecordingDoneConfirmation
                  }
                  isUserLoggedIn={!!user}
                  onPressDoneRecordingConfirmation={
                    onPressDoneRecordingConfirmation
                  }
                  coverartImageUrl={hostPodcastImage}
                />
              );
            case 'maximumReached':
              return (
                <CallEndedOrFull
                  scene="full"
                  podcastCreatorUserName={hostName}
                  podcastName={hostPodcastName}
                  isShowingLeaveConfirmationModal={
                    isShowingRecordingDoneConfirmation
                  }
                  isUserLoggedIn={!!user}
                  onPressDoneRecordingConfirmation={
                    onPressDoneRecordingConfirmation
                  }
                  coverartImageUrl={hostPodcastImage}
                />
              );
            default:
              return <h1>error</h1>;
          }
        })()}
      </Box>
    );
  }
}

export default CallJoin;
